import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../shared/components/asset-header/asset-header.component';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import {
  LibraryItem,
  LibraryContentType,
  LibrariesJson,
  LIBRARIES_JSON_URLS,
  mapLibrariesJsonToItems
} from './library-items.data';

@Component({
  selector: 'app-libraries',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PaginatorModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
  ],
  templateUrl: './libraries.component.html',
  styleUrl: './libraries.component.scss',
})
export class LibrariesComponent implements OnInit {
  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Library' }
    ],
    title: 'Library',
    description: 'A secure environment to explore, test, and integrate APIs, data services, and platform capabilities. A controlled testing space for partners and developers to build and validate agri-data solutions on MahaAgX.'
  };

  allItems: LibraryItem[] = [];
  loading = true;
  loadError: string | null = null;
  activeTab: LibraryContentType = 'article';
  /** 'grid' = card grid (with thumbnails), 'table' = table rows. Grid is default for videos. */
  viewMode: 'grid' | 'table' = 'grid';

  readonly pageSize = 8;
  currentPage = 1;
  /** Filters the current tab’s items by title, description, and last updated. */
  searchQuery = '';

  private readonly tabQueryKey = 'tab';

  /** Items for the active tab only (before search). */
  get tabItems(): LibraryItem[] {
    return this.allItems.filter(item => item.type === this.activeTab);
  }

  get filteredItems(): LibraryItem[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) {
      return this.tabItems;
    }
    return this.tabItems.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        (item.lastUpdated && item.lastUpdated.toLowerCase().includes(q))
    );
  }

  get hasActiveSearch(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  get searchPlaceholder(): string {
    return this.activeTab === 'article' ? 'Search articles' : 'Search video';
  }

  get displayedItems(): LibraryItem[] {
    const items = this.filteredItems;
    const start = (this.currentPage - 1) * this.pageSize;
    return items.slice(start, start + this.pageSize);
  }

  get shouldShowPagination(): boolean {
    return (
      !this.loading &&
      !this.loadError &&
      this.filteredItems.length > this.pageSize
    );
  }

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Restore tab from URL so refresh keeps the user on the same tab (e.g. video)
    const tab = this.route.snapshot.queryParamMap.get(this.tabQueryKey);
    if (tab === 'video' || tab === 'article') {
      this.activeTab = tab;
    }
    this.route.queryParams.subscribe((params) => {
      const q = params[this.tabQueryKey];
      if (q === 'video' || q === 'article') {
        this.activeTab = q;
      }
    });
    this.tryLoadFromUrls(0);
  }

  private tryLoadFromUrls(index: number): void {
    if (index >= LIBRARIES_JSON_URLS.length) {
      this.loadError = 'Failed to load library content.';
      this.loading = false;
      return;
    }
    const url = LIBRARIES_JSON_URLS[index];
    this.http.get<LibrariesJson>(url).subscribe({
      next: (data) => {
        this.allItems = mapLibrariesJsonToItems(data);
        this.loading = false;
      },
      error: () => {
        this.tryLoadFromUrls(index + 1);
      }
    });
  }

  setTab(type: LibraryContentType): void {
    this.activeTab = type;
    this.currentPage = 1;
    // Update URL so refresh keeps the user on this tab
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [this.tabQueryKey]: type },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  setViewMode(mode: 'grid' | 'table'): void {
    this.viewMode = mode;
  }

  onSearchChange(): void {
    this.currentPage = 1;
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.currentPage = 1;
  }

  onPageChange(event: PaginatorState): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize;
    if (rows <= 0) {
      return;
    }
    this.currentPage = Math.floor(first / rows) + 1;
  }

  /** Fallback when thumbnail fails to load (e.g. staging CSP or network). */
  onThumbnailError(event: Event): void {
    const img = event?.target as HTMLImageElement | null;
    if (img) {
      img.style.background = '#f3f4f6';
      img.src = '';
    }
  }
}
